import React, { useState, useEffect, useMemo } from 'react';
import { getPortalSuggestionsSummary } from '@/lib/suggestionsV2Client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { 
  Building2, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle2, 
  Clock, 
  XCircle,
  Filter,
  ArrowUpDown,
  ChevronRight
} from 'lucide-react';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';

// Types for Portal Suggestions Summary V2 response
type SuggestionStatus = 'todo' | 'in_progress' | 'verifica_in_corso' | 'done' | 'rejected';

type StatusCounts = {
  todo: number;
  in_progress: number;
  verifica_in_corso: number;
  done: number;
  rejected: number;
};

type StructureSummary = {
  structureId: string;
  structureName?: string;
  completionRate: number;
  counts: StatusCounts;
  source: 'collection' | 'legacy' | 'none';
};

type PortalSummaryResponse = {
  totalStructures: number;
  structuresWithSuggestions: number;
  totalItems: number;
  byStatus: StatusCounts;
  structures: StructureSummary[];
  nextCursor?: string;
};

interface PortalMonitorV2Props {
  portalName: string;
  onOpenStructure?: (structureId: string) => void;
}

export function PortalMonitorV2({ portalName, onOpenStructure }: PortalMonitorV2Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PortalSummaryResponse | null>(null);
  const [structures, setStructures] = useState<StructureSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filters
  const [showOnlyIncomplete, setShowOnlyIncomplete] = useState(false);
  const [showOnlyWithTodo, setShowOnlyWithTodo] = useState(false);
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'completionRate' | 'todo'>('completionRate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const PAGE_SIZE = 20;

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await getPortalSuggestionsSummary(portalName, {
          limit: PAGE_SIZE,
          includeEmpty: includeEmpty
        });

        if (!response || typeof response !== 'object') {
          throw new Error('Invalid response format');
        }

        const summaryData = response as PortalSummaryResponse;
        setData(summaryData);
        setStructures(summaryData.structures || []);
        setNextCursor(summaryData.nextCursor);
      } catch (err: any) {
        console.error('Error loading portal suggestions summary:', err);
        setError(err?.message || 'Failed to load suggestions summary');
      } finally {
        setLoading(false);
      }
    };

    if (portalName) {
      loadInitialData();
    }
  }, [portalName, includeEmpty]);

  // Load more (pagination)
  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;

    setLoadingMore(true);
    try {
      const response = await getPortalSuggestionsSummary(portalName, {
        limit: PAGE_SIZE,
        cursor: nextCursor,
        includeEmpty: includeEmpty
      });

      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format');
      }

      const summaryData = response as PortalSummaryResponse;
      setStructures(prev => [...prev, ...(summaryData.structures || [])]);
      setNextCursor(summaryData.nextCursor);
    } catch (err: any) {
      console.error('Error loading more structures:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Calculate global completion rate
  const globalCompletionRate = useMemo(() => {
    if (!data) return 0;
    const { byStatus } = data;
    const total = byStatus.todo + byStatus.in_progress + byStatus.done + byStatus.rejected;
    const totalMinusRejected = total - byStatus.rejected;
    if (totalMinusRejected === 0) return 0;
    return Math.round((byStatus.done / totalMinusRejected) * 100);
  }, [data]);

  // Filter and sort structures
  const filteredAndSortedStructures = useMemo(() => {
    let result = [...structures];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s => 
        (s.structureName || s.structureId).toLowerCase().includes(query)
      );
    }

    // Apply "only incomplete" filter
    if (showOnlyIncomplete) {
      result = result.filter(s => s.completionRate < 100);
    }

    // Apply "only with todo" filter
    if (showOnlyWithTodo) {
      result = result.filter(s => s.counts.todo > 0);
    }

    // Apply sorting
    result.sort((a, b) => {
      let compareValue = 0;
      
      if (sortBy === 'completionRate') {
        compareValue = a.completionRate - b.completionRate;
      } else if (sortBy === 'todo') {
        compareValue = a.counts.todo - b.counts.todo;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return result;
  }, [structures, searchQuery, showOnlyIncomplete, showOnlyWithTodo, sortBy, sortOrder]);

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-8 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h3 className="text-l font-semibold mb-2">Errore</h3>
            <p className="text-s text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>
              Riprova
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!data || structures.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-l font-semibold mb-2">Nessuna struttura trovata</h3>
            <p className="text-s text-muted-foreground">
              {includeEmpty 
                ? 'Non ci sono strutture nel portale.'
                : 'Nessuna struttura con suggestions trovata. Attiva "Mostra anche vuote" per vedere tutte le strutture.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getSourceBadge = (source: string) => {
    switch (source) {
      case 'collection':
        return <Badge variant="outline" className="bg-green-50 border-green-200 text-green-700">V2</Badge>;
      case 'legacy':
        return <Badge variant="outline" className="bg-orange-50 border-orange-200 text-orange-700">Legacy</Badge>;
      default:
        return <Badge variant="outline" className="bg-gray-50 border-gray-200 text-gray-700">Nessuna</Badge>;
    }
  };

  const getStatusIcon = (status: SuggestionStatus) => {
    switch (status) {
      case 'todo':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      case 'in_progress':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'done':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Global KPIs */}
      <Card>
        <CardHeader>
          <CardTitle>Panoramica Suggestions Portale</CardTitle>
          <CardDescription>Monitoraggio globale suggestions per {portalName}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-1">
              <div className="text-s text-muted-foreground">Strutture totali</div>
              <div className="text-xxl font-bold">{data.totalStructures}</div>
            </div>
            
            <div className="space-y-1">
              <div className="text-s text-muted-foreground">Con suggestions</div>
              <div className="text-xxl font-bold">{data.structuresWithSuggestions}</div>
            </div>
            
            <div className="space-y-1">
              <div className="text-s text-muted-foreground">Suggestions totali</div>
              <div className="text-xxl font-bold">{data.totalItems}</div>
            </div>
            
            <div className="space-y-1">
              <div className="text-s text-muted-foreground">Completion rate</div>
              <div className="text-xxl font-bold flex items-center gap-2">
                {globalCompletionRate}%
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-s text-muted-foreground">Per stato</div>
              <div className="grid grid-cols-2 gap-2 text-ss">
                <div className="flex items-center gap-1">
                  {getStatusIcon('todo')}
                  <span>{data.byStatus.todo} da fare</span>
                </div>
                <div className="flex items-center gap-1">
                  {getStatusIcon('in_progress')}
                  <span>{data.byStatus.in_progress} in corso</span>
                </div>
                <div className="flex items-center gap-1">
                  {getStatusIcon('done')}
                  <span>{data.byStatus.done} fatte</span>
                </div>
                <div className="flex items-center gap-1">
                  {getStatusIcon('rejected')}
                  <span>{data.byStatus.rejected} scartate</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtri e Ordinamento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label>Cerca struttura</Label>
              <Input
                placeholder="Nome struttura..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Sort by */}
            <div className="space-y-2">
              <Label>Ordina per</Label>
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as 'completionRate' | 'todo')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completionRate">Completion rate</SelectItem>
                  <SelectItem value="todo">Numero da fare</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort order */}
            <div className="space-y-2">
              <Label>Direzione</Label>
              <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as 'asc' | 'desc')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Crescente</SelectItem>
                  <SelectItem value="desc">Decrescente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Toggle filters */}
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-2">
              <Switch
                id="only-incomplete"
                checked={showOnlyIncomplete}
                onCheckedChange={setShowOnlyIncomplete}
              />
              <Label htmlFor="only-incomplete" className="cursor-pointer">
                Solo incomplete
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="only-todo"
                checked={showOnlyWithTodo}
                onCheckedChange={setShowOnlyWithTodo}
              />
              <Label htmlFor="only-todo" className="cursor-pointer">
                Solo con da fare
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="include-empty"
                checked={includeEmpty}
                onCheckedChange={setIncludeEmpty}
              />
              <Label htmlFor="include-empty" className="cursor-pointer">
                Mostra anche vuote
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Structures Table */}
      <Card>
        <CardHeader>
          <CardTitle>Strutture ({filteredAndSortedStructures.length})</CardTitle>
          <CardDescription>
            {sortBy === 'completionRate' && sortOrder === 'asc' && 
              'Ordinate per priorità (completion rate crescente)'}
            {sortBy === 'completionRate' && sortOrder === 'desc' && 
              'Ordinate per completion rate decrescente'}
            {sortBy === 'todo' && 'Ordinate per numero di interventi da fare'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredAndSortedStructures.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-s text-muted-foreground">
                Nessuna struttura corrisponde ai filtri selezionati
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAndSortedStructures.map((structure) => (
                <Card key={structure.structureId} className="shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-medium truncate">
                            {structure.structureName || structure.structureId}
                          </h4>
                          {getSourceBadge(structure.source)}
                        </div>
                        
                        <div className="flex items-center gap-4 text-s">
                          <div className="flex items-center gap-1">
                            {getStatusIcon('todo')}
                            <span className="text-muted-foreground">{structure.counts.todo}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {getStatusIcon('in_progress')}
                            <span className="text-muted-foreground">{structure.counts.in_progress}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {getStatusIcon('done')}
                            <span className="text-muted-foreground">{structure.counts.done}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-xxl font-bold">
                            {structure.completionRate}%
                          </div>
                          <div className="text-ss text-muted-foreground">completamento</div>
                        </div>

                        {onOpenStructure && (
                          <Button
                            size="sm"
                            onClick={() => onOpenStructure(structure.structureId)}
                          >
                            Apri struttura
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Load More Button */}
          {nextCursor && (
            <div className="mt-6 text-center">
              <Button
                onClick={handleLoadMore}
                disabled={loadingMore}
                variant="outline"
              >
                {loadingMore ? 'Caricamento...' : 'Carica altro'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
